import { useState, useEffect } from 'react'
import { useOutletContext } from 'react-router-dom'
import type { Settings as SettingsType, AIProvider, DemoModeState } from '@shared/types'
import { GCC_SETTINGS_GUIDES } from '@shared/guides'
import { useApi } from '../index'

const GCC_COLOR = '#059669'

interface GccLayoutContext {
  refreshProjects: () => Promise<void>
}

export default function GccSettings() {
  const [settings, setSettings] = useState<SettingsType>({
    aiProvider: 'claude',
    anthropicApiKey: '',
    openaiApiKey: '',
    azureEndpoint: '',
    azureApiKey: '',
    azureDeployment: '',
    azureApiVersion: '2024-12-01-preview',
  })
  const [demoState, setDemoState] = useState<DemoModeState | null>(null)
  const [togglingDemo, setTogglingDemo] = useState(false)
  const [demoError, setDemoError] = useState('')
  const [saved, setSaved] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null)
  const [guideError, setGuideError] = useState('')

  const api = useApi()
  const { refreshProjects } = useOutletContext<GccLayoutContext>()
  const isWeb = api.platform === 'web'

  useEffect(() => {
    api.getSettings().then(setSettings).catch(() => {})
    api.getDemoState().then(setDemoState).catch(() => {})
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount; api is a stable context reference
  }, [])

  const toggleDemo = async () => {
    if (togglingDemo) return
    setTogglingDemo(true)
    setDemoError('')
    const prevActive = demoState?.active ?? false
    const newActive = !prevActive
    try {
      const result = await api.setDemoActive(newActive)
      if (!result.success) {
        setDemoError('Server declined the demo toggle. Please try again.')
        return
      }
      setDemoState(prev => prev ? { ...prev, active: newActive } : { active: newActive, source: 'settings' })
      try {
        if (newActive) await api.seedGccDemo?.()
        else await api.resetGccDemo?.()
      } catch (seedErr) {
        // Roll back the active flag so UI and server stay in sync
        await api.setDemoActive(prevActive).catch(() => {})
        setDemoState(prev => prev ? { ...prev, active: prevActive } : { active: prevActive, source: 'settings' })
        throw seedErr
      }
      await refreshProjects()
    } catch (err) {
      setDemoError(err instanceof Error ? err.message : 'Demo toggle failed. Please try again.')
    } finally {
      setTogglingDemo(false)
    }
  }

  const save = async () => {
    await api.setSettings(settings)
    setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const testConnection = async () => {
    setTesting(true)
    setTestResult(null)
    await api.setSettings(settings)
    const apiKey =
      settings.aiProvider === 'claude' ? settings.anthropicApiKey
      : settings.aiProvider === 'azure-openai' ? settings.azureApiKey
      : settings.openaiApiKey
    const ok = await api.testConnection(settings.aiProvider, apiKey)
    setTestResult({ ok, msg: ok ? 'Connection successful!' : 'Connection failed — check your configuration.' })
    setTesting(false)
  }

  const openGuide = api.openGuide?.bind(api)
  const showGuide = async (open: () => Promise<void>) => {
    setGuideError('')
    try {
      await open()
    } catch {
      setGuideError('The guide could not be opened. Please try again.')
    }
  }

  const aiProviderLabel = (p: string) =>
    p === 'claude' ? '⚡ Claude (Anthropic)'
    : p === 'openai' ? '🧠 GPT-4o (OpenAI)'
    : '☁️ Azure OpenAI'

  const canToggleDemo = demoState?.source !== 'cli' && demoState?.source !== 'env'

  return (
    <div className="p-8 max-w-xl">
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">GCC Settings</h1>
      <p className="text-sm text-gray-500 mb-8">
        {isWeb
          ? 'Toggle demo mode and view configuration for the Global Capability Center.'
          : 'Configure your AI provider and demo mode for GCC generation.'}
      </p>

      {/* Demo Mode */}
      <div className="mb-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Demo Mode</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {demoState?.active
                ? 'Active — pre-built GCC designs are visible.'
                : 'When enabled, sample GCC designs are shown for demonstrations.'}
            </p>
          </div>
          <button
            role="switch"
            aria-checked={demoState?.active ?? false}
            aria-label="Demo Mode"
            onClick={() => void toggleDemo()}
            disabled={togglingDemo || !canToggleDemo}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              demoState?.active ? 'bg-amber-500' : 'bg-gray-300'
            } ${togglingDemo || !canToggleDemo ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                demoState?.active ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        {demoError && (
          <p className="text-xs text-red-600 mt-2">{demoError}</p>
        )}
        {!canToggleDemo && (
          <p className="text-[10px] text-amber-600 mt-1">
            Activated via {demoState?.source === 'cli' ? '--demo flag' : 'TALENT_NAV_DEMO environment variable'} — cannot be toggled here.
          </p>
        )}
      </div>

      {/* AI Provider — read-only on web */}
      {isWeb ? (
        <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700 mb-1">AI Provider</h3>
          <p className="text-xs text-gray-500 mb-2">
            Configured by the server administrator. Contact your admin to change the AI provider.
          </p>
          <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
            <span>{aiProviderLabel(settings.aiProvider)}</span>
            <span className="text-xs text-emerald-600 font-normal px-2 py-0.5 bg-emerald-50 rounded-full border border-emerald-200">
              Server-configured
            </span>
          </div>
        </div>
      ) : (
        <>
          {/* Desktop: full provider + key config */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">AI Provider</label>
            <div className="flex gap-3">
              {(['claude', 'openai', 'azure-openai'] as AIProvider[]).map(p => (
                <button
                  key={p}
                  onClick={() => setSettings(s => ({ ...s, aiProvider: p }))}
                  className={`flex-1 py-2.5 px-3 rounded-lg border-2 text-xs font-semibold transition-all ${
                    settings.aiProvider === p
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300'
                  }`}
                >
                  {p === 'claude' ? '⚡ Claude' : p === 'openai' ? '🧠 GPT-4o' : '☁️ Azure OpenAI'}
                </button>
              ))}
            </div>
          </div>

          {settings.aiProvider === 'claude' && (
            <KeyField
              label="Anthropic API Key"
              value={settings.anthropicApiKey}
              onChange={v => setSettings(s => ({ ...s, anthropicApiKey: v }))}
              placeholder="Enter your Anthropic API key"
            />
          )}
          {settings.aiProvider === 'openai' && (
            <KeyField
              label="OpenAI API Key"
              value={settings.openaiApiKey}
              onChange={v => setSettings(s => ({ ...s, openaiApiKey: v }))}
              placeholder="sk-..."
            />
          )}
          {settings.aiProvider === 'azure-openai' && (
            <>
              <TextField
                label="Azure Endpoint"
                value={settings.azureEndpoint}
                onChange={v => setSettings(s => ({ ...s, azureEndpoint: v }))}
                placeholder="https://your-resource.openai.azure.com/"
              />
              <KeyField
                label="Azure API Key"
                value={settings.azureApiKey}
                onChange={v => setSettings(s => ({ ...s, azureApiKey: v }))}
                placeholder="your-azure-api-key"
              />
              <TextField
                label="Deployment Name"
                value={settings.azureDeployment}
                onChange={v => setSettings(s => ({ ...s, azureDeployment: v }))}
                placeholder="csdep-30429803-gpt-4o"
              />
            </>
          )}

          <div className="flex gap-3 mt-6">
            <button
              onClick={() => void testConnection()}
              disabled={testing}
              className="flex-1 py-2.5 px-4 rounded-lg border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:border-gray-300 transition-all disabled:opacity-50"
            >
              {testing ? 'Testing…' : 'Test Connection'}
            </button>
            <button
              onClick={() => void save()}
              className="flex-1 py-2.5 px-4 rounded-lg text-white text-sm font-semibold transition-all hover:opacity-90"
              style={{ background: GCC_COLOR }}
            >
              {saved ? '✓ Saved' : 'Save Settings'}
            </button>
          </div>

          {testResult && (
            <div className={`mt-4 p-3 rounded-lg text-sm font-medium ${
              testResult.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
            }`}>
              {testResult.ok ? '✓ ' : '✗ '}{testResult.msg}
            </div>
          )}
        </>
      )}

      {/* User guides (WI 2887): web only, where the client can open them */}
      {openGuide && (
        <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700 mb-1">User guides</h3>
          <p className="text-xs text-gray-500 mb-2">Each guide opens in a new tab.</p>
          <ul className="space-y-1">
            {GCC_SETTINGS_GUIDES.map(g => (
              <li key={g.id}>
                <button
                  type="button"
                  onClick={() => void showGuide(() => openGuide(g.id))}
                  className="text-sm font-medium text-emerald-700 underline underline-offset-2 hover:text-emerald-900"
                >
                  {g.title}
                </button>
              </li>
            ))}
          </ul>
          {guideError && <p role="alert" className="text-xs text-red-600 mt-2">{guideError}</p>}
        </div>
      )}
    </div>
  )
}

function KeyField({ label, value, onChange, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string
}) {
  return (
    <div className="mb-5">
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>
      <input
        type="password"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-200 bg-white font-mono"
      />
    </div>
  )
}

function TextField({ label, value, onChange, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string
}) {
  return (
    <div className="mb-5">
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-200 bg-white font-mono"
      />
    </div>
  )
}
